import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Papa from 'papaparse';
import { useBadges } from '../hooks/useApi';
import { api } from '../api/client';
import { BadgePicker } from '../components/BadgePicker';
import { useBulkAwardProcessor, type BulkRecipient } from '../hooks/useBulkAwardProcessor';
import type { Badge } from '../types';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadTemplate() {
  downloadCsv('bulk-awards-template.csv', [
    'name,email',
    'Jane Smith,jane.smith@example.com',
    'John Doe,john.doe@example.com',
    'Maria Garcia,maria.garcia@example.com',
  ].join('\n'));
}

interface ParsedRow {
  row: number;
  name: string;
  email: string;
  valid: boolean;
  reason?: string;
}

function parseUpload(file: File): Promise<ParsedRow[]> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete(results) {
        const fields = (results.meta.fields ?? []).map(f => f.toLowerCase().trim());
        const nameCol = fields.find(f => f === 'name' || f === 'recipient_name');
        const emailCol = fields.find(f => f === 'email' || f === 'recipient_email');
        if (!nameCol || !emailCol) {
          reject(new Error('CSV must contain "name" (or "recipient_name") and "email" (or "recipient_email") columns.'));
          return;
        }

        const rows: ParsedRow[] = (results.data as Record<string, string>[]).map((r, i) => {
          const raw: Record<string, string> = {};
          for (const key of Object.keys(r)) raw[key.toLowerCase().trim()] = r[key];

          const name = (raw[nameCol] ?? '').trim();
          const email = (raw[emailCol] ?? '').trim();
          let valid = true;
          let reason: string | undefined;

          if (!name || name.length < 5) {
            valid = false;
            reason = !name ? 'Name is required' : 'Name must be at least 5 characters';
          } else if (!email || !EMAIL_RE.test(email)) {
            valid = false;
            reason = !email ? 'Email is required' : 'Invalid email format';
          }
          return { row: i + 1, name, email, valid, reason };
        });
        resolve(rows);
      },
      error: reject,
    });
  });
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export function BulkAwardsPage() {
  const { t } = useTranslation('dashboard');

  /* --- data --- */
  const { data: rawBadges } = useBadges();
  const badges = useMemo<Badge[]>(() => {
    if (!rawBadges) return [];
    return Array.isArray(rawBadges) ? rawBadges : ((rawBadges as any).items ?? []);
  }, [rawBadges]);

  /* --- wizard state --- */
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Step 1
  const [selectedBadge, setSelectedBadge] = useState('');
  const [issuedDate, setIssuedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [expiresDate, setExpiresDate] = useState('');
  const [sendEmail, setSendEmail] = useState(true);

  // Step 2
  const [validRecipients, setValidRecipients] = useState<ParsedRow[]>([]);
  const [invalidRecipients, setInvalidRecipients] = useState<ParsedRow[]>([]);
  const [parseError, setParseError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  // Step 3-4
  const processor = useBulkAwardProcessor();

  // TICKET-3 — duplicate detection: which of the CSV recipients already hold
  // the selected badge, so we don't silently re-issue on a re-import.
  const [existingRecipients, setExistingRecipients] = useState<Set<string>>(new Set());
  const [skipDupes, setSkipDupes] = useState(true);
  const [dupeChecking, setDupeChecking] = useState(false);

  useEffect(() => {
    if (step !== 2 || !selectedBadge || validRecipients.length === 0) return;
    let cancelled = false;
    (async () => {
      setDupeChecking(true);
      try {
        const emails = new Set<string>();
        let lek: string | undefined;
        let guard = 0;
        do {
          const resp = await api.listAwards({ badgeId: selectedBadge }, lek);
          for (const a of resp.items) if (a.recipientEmail) emails.add(a.recipientEmail.toLowerCase());
          lek = resp.lastEvaluatedKey;
        } while (lek && guard++ < 300 && !cancelled);
        if (!cancelled) setExistingRecipients(emails);
      } catch { /* dedupe is best-effort; never block the import */ }
      finally { if (!cancelled) setDupeChecking(false); }
    })();
    return () => { cancelled = true; };
  }, [step, selectedBadge, validRecipients.length]);

  const dupeRecipients = useMemo(
    () => validRecipients.filter(r => existingRecipients.has(r.email.toLowerCase())),
    [validRecipients, existingRecipients]
  );

  /* --- file handling --- */
  const handleFile = useCallback(async (file: File | undefined) => {
    if (!file) return;
    setParseError('');
    try {
      const rows = await parseUpload(file);
      setValidRecipients(rows.filter(r => r.valid));
      setInvalidRecipients(rows.filter(r => !r.valid));
    } catch (err: any) {
      setParseError(err.message || 'Failed to parse CSV');
      setValidRecipients([]);
      setInvalidRecipients([]);
    }
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  /* --- step transitions --- */
  function goToProcess() {
    // Skip recipients who already hold this badge unless the user opts to re-issue.
    const source = skipDupes ? validRecipients.filter(r => !existingRecipients.has(r.email.toLowerCase())) : validRecipients;
    const recipients: BulkRecipient[] = source.map((r, i) => ({ name: r.name, email: r.email, rowIndex: i }));
    const issuedTs = new Date(issuedDate).getTime();
    const expiresTs = expiresDate ? new Date(expiresDate).getTime() : undefined;
    const newState = processor.initProcess(selectedBadge, recipients, issuedTs, expiresTs, sendEmail);
    setStep(3);
    processor.start(newState);
  }

  function retryFailures() {
    const failed = (processor.state?.results ?? []).filter((r: any) => r.status === 'failed');
    const recipients: BulkRecipient[] = failed.map((r: any, i: number) => ({ name: r.recipient.name, email: r.recipient.email, rowIndex: i }));
    const issuedTs = new Date(issuedDate).getTime();
    const expiresTs = expiresDate ? new Date(expiresDate).getTime() : undefined;
    const newState = processor.initProcess(selectedBadge, recipients, issuedTs, expiresTs, sendEmail);
    setStep(3);
    processor.start(newState);
  }

  function resetWizard() {
    setStep(1);
    setSelectedBadge('');
    setIssuedDate(new Date().toISOString().split('T')[0]);
    setExpiresDate('');
    setSendEmail(true);
    setValidRecipients([]);
    setInvalidRecipients([]);
    setParseError('');
    processor.reset();
  }

  function downloadResultsCsv() {
    const header = 'row,name,email,status,award_url';
    const lines = (processor.state?.results ?? []).map((r, i) =>
      [i + 1, `"${r.recipient.name}"`, `"${r.recipient.email}"`, r.status, r.awardId ?? ''].join(',')
    );
    downloadCsv(`bulk-awards-results-${new Date().toISOString().split('T')[0]}.csv`, [header, ...lines].join('\n'));
  }

  function downloadFailuresCsv() {
    const failed = (processor.state?.results ?? []).filter(r => r.status === 'failed');
    const header = 'name,email,error';
    const esc = (s: string) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const lines = failed.map(r => `${esc(r.recipient.name)},${esc(r.recipient.email)},${esc(r.error || 'Unknown error')}`);
    downloadCsv(`bulk-awards-failures-${new Date().toISOString().split('T')[0]}.csv`, [header, ...lines].join('\n'));
  }

  /* --- selected badge preview --- */
  const badgeObj = useMemo(() => badges.find(b => b.id === selectedBadge || b.id.split('/').pop() === selectedBadge), [badges, selectedBadge]);

  /* --- resumable job check on Step 3 mount --- */
  const resumeChecked = useRef(false);
  if (step === 3 && !resumeChecked.current) {
    resumeChecked.current = true;
    if (processor.hasResumableJob) {
      // offer handled via UI below
    }
  }

  /* ---------------------------------------------------------------- */
  /*  Render                                                          */
  /* ---------------------------------------------------------------- */

  return (
    <div className="min-h-full p-4 sm:p-8 animate-fade-in">
      <div className="page-wide">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('bulkAwards.title', 'Bulk Awards')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('bulkAwards.subtitle', 'Award a badge to many recipients at once.')}</p>
        </div>

        {/* Step indicator */}
        <div className="mb-8 flex items-center gap-2">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                s === step ? 'bg-primary-600 text-white' : s < step ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-400'
              }`}>
                {s < step ? (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                ) : s}
              </div>
              {s < 4 && <div className={`h-0.5 w-8 sm:w-12 rounded ${s < step ? 'bg-primary-400' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {/* ============ STEP 1 ============ */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-card">
              <h2 className="text-base font-semibold text-gray-900 mb-4">{t('bulkAwards.selectBadge', 'Select Badge')}</h2>
              <BadgePicker badges={badges} selected={selectedBadge} onSelect={setSelectedBadge} placeholder={t('awards.selectBadge', 'Select a badge...')} />
              {badgeObj && (
                <div className="mt-4 flex items-center gap-3 rounded-xl border border-primary-100 bg-primary-50/50 p-3">
                  <img src={badgeObj.image} alt="" className="h-14 w-14 rounded-lg object-contain bg-white" onError={e => { (e.target as HTMLImageElement).src = '/assets/badge-templates/Template-000.png'; }} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{badgeObj.name}</p>
                    <p className="text-xs text-gray-500 truncate">{badgeObj.issuer?.name}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-card">
              <h2 className="text-base font-semibold text-gray-900 mb-4">{t('bulkAwards.options', 'Options')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('awards.issuedDate', 'Issued')}</label>
                  <input type="date" value={issuedDate} onChange={e => setIssuedDate(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t('awards.expiresOptional', 'Expires (optional)')}</label>
                  <input type="date" value={expiresDate} onChange={e => setExpiresDate(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer mt-4">
                <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                <span className="text-sm text-gray-700">{t('awards.sendEmailOnCreate', 'Send email notification')}</span>
              </label>
            </div>

            <div className="flex justify-end">
              <button onClick={() => setStep(2)} disabled={!selectedBadge} className="rounded-xl bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-50">
                {t('bulkAwards.next', 'Next')}
              </button>
            </div>
          </div>
        )}

        {/* ============ STEP 2 ============ */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Instructions */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-card">
              <h2 className="text-base font-semibold text-gray-900 mb-2">{t('bulkAwards.uploadTitle', 'Upload Recipients')}</h2>
              <p className="text-sm text-gray-500 mb-4">{t('bulkAwards.uploadDescription', 'Upload a CSV file with two columns: name and email. One recipient per row.')}</p>
              <button onClick={downloadTemplate} className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 active:scale-[0.98]">
                {t('bulkAwards.downloadTemplate', 'Download Template')}
              </button>
            </div>

            {/* Drop zone */}
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className={`cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${
                dragOver ? 'border-primary-400 bg-primary-50/40' : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            >
              <svg className="mx-auto h-10 w-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
              <p className="mt-3 text-sm text-gray-600">{t('bulkAwards.dropZone', 'Drag and drop a CSV file here, or click to browse')}</p>
              <p className="mt-1 text-xs text-gray-400">{t('bulkAwards.csvOnly', '.csv files only')}</p>
              <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
            </div>

            {parseError && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{parseError}</div>
            )}

            {/* Preview table */}
            {(validRecipients.length > 0 || invalidRecipients.length > 0) && (
              <div className="rounded-2xl border border-gray-200 bg-white shadow-card overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-emerald-600">{validRecipients.length}</span>{' '}
                    {t('bulkAwards.validRecipients', 'valid recipients')}
                    {invalidRecipients.length > 0 && (
                      <>, <span className="font-semibold text-red-500">{invalidRecipients.length}</span>{' '}{t('bulkAwards.invalidSkipped', 'invalid (will be skipped)')}</>
                    )}
                  </p>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">#</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colName', 'Name')}</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colEmail', 'Email')}</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colValid', 'Valid')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...validRecipients, ...invalidRecipients]
                        .sort((a, b) => a.row - b.row)
                        .map(r => (
                          <tr key={r.row} className="border-t border-gray-50">
                            <td className="px-4 py-2 text-xs text-gray-400">{r.row}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{r.name || '--'}</td>
                            <td className="px-4 py-2 text-sm text-gray-700">{r.email || '--'}</td>
                            <td className="px-4 py-2">
                              {r.valid ? (
                                <span className="text-emerald-500" title="Valid">&#10003;</span>
                              ) : (
                                <span className="text-red-500 text-xs" title={r.reason}>&#10007; {r.reason}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TICKET-3 — duplicate warning */}
            {dupeChecking && validRecipients.length > 0 && (
              <p className="text-xs text-gray-400">{t('bulkAwards.dupeChecking', 'Checking for recipients who already have this badge…')}</p>
            )}
            {dupeRecipients.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-medium text-amber-900">
                  {t('bulkAwards.dupeTitle', '{{n}} of these recipients already have this badge.', { n: dupeRecipients.length })}
                </p>
                <label className="mt-2 flex items-center gap-2 text-xs text-amber-900 cursor-pointer">
                  <input type="checkbox" checked={skipDupes} onChange={e => setSkipDupes(e.target.checked)} className="rounded border-amber-300 text-amber-600 focus:ring-amber-500" />
                  {t('bulkAwards.dupeSkip', 'Skip duplicates (recommended) — issue only to the {{n}} new recipients', { n: validRecipients.length - dupeRecipients.length })}
                </label>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="rounded-xl border border-gray-200 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 active:scale-[0.98]">
                {t('bulkAwards.back', 'Back')}
              </button>
              <button onClick={goToProcess} disabled={validRecipients.length === 0 || (skipDupes && validRecipients.length - dupeRecipients.length === 0)} className="rounded-xl bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98] disabled:opacity-50">
                {t('bulkAwards.next', 'Next')}
              </button>
            </div>
          </div>
        )}

        {/* ============ STEP 3 ============ */}
        {step === 3 && (
          <div className="space-y-6">
            {/* Resumable job prompt */}
            {processor.hasResumableJob && processor.state?.status === 'idle' && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-center justify-between">
                <p className="text-sm text-amber-800">{t('bulkAwards.resumableJob', 'A previous batch was interrupted. Would you like to resume?')}</p>
                <button onClick={() => processor.resume()} className="rounded-lg bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-700 transition-colors">
                  {t('bulkAwards.resumeJob', 'Resume')}
                </button>
              </div>
            )}

            {/* Progress bar */}
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-card">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-semibold text-gray-900">{t('bulkAwards.processing', 'Processing')}</h2>
                <span className="text-sm font-medium text-gray-600">
                  {processor.stats.created + processor.stats.failed} / {processor.stats.total}
                </span>
              </div>
              <div className="h-3 w-full rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary-500 to-indigo-500 transition-all duration-300"
                  style={{ width: processor.stats.total > 0 ? `${((processor.stats.created + processor.stats.failed) / processor.stats.total) * 100}%` : '0%' }}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500 text-right">
                {processor.stats.total > 0 ? `${Math.round(((processor.stats.created + processor.stats.failed) / processor.stats.total) * 100)}%` : '0%'}
              </p>

              {/* Counters */}
              <div className="mt-4 flex gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  <span className="text-xs text-gray-600">{t('bulkAwards.created', 'Created')}: <span className="font-semibold text-emerald-600">{processor.stats.created}</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
                  <span className="text-xs text-gray-600">{t('bulkAwards.failed', 'Failed')}: <span className="font-semibold text-red-600">{processor.stats.failed}</span></span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                  <span className="text-xs text-gray-600">
                    {processor.state?.status === 'cancelled'
                      ? t('bulkAwards.skipped', 'Skipped')
                      : t('bulkAwards.remaining', 'Remaining')}
                    : <span className="font-semibold text-gray-500">{processor.stats.total - (processor.stats.created + processor.stats.failed)}</span>
                  </span>
                </div>
              </div>

              {/* Paused / credits message */}
              {processor.state?.status === 'paused' && (
                <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm text-amber-800">
                  {t('bulkAwards.paused', 'Paused — click Resume to continue')}
                </div>
              )}
              {processor.state?.status === 'paused' && (
                <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">
                  {t('bulkAwards.creditsExhausted', 'Insufficient credits — upgrade plan to continue')}
                </div>
              )}

              {/* Controls */}
              <div className="mt-4 flex gap-2">
                {processor.state?.status === 'running' && (
                  <button onClick={() => processor.pause()} className="rounded-lg bg-amber-500 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-600 transition-colors">
                    {t('bulkAwards.pause', 'Pause')}
                  </button>
                )}
                {(['paused'] as string[]).includes(processor.state?.status ?? '') && (
                  <button onClick={() => processor.resume()} className="rounded-lg bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors">
                    {t('bulkAwards.resume', 'Resume')}
                  </button>
                )}
                {(processor.state?.status === 'running' || processor.state?.status === 'paused') && (
                  <button onClick={() => processor.cancel()} className="rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 transition-colors">
                    {t('bulkAwards.cancel', 'Cancel')}
                  </button>
                )}
                {(processor.state?.status === 'done' || processor.state?.status === 'cancelled') && (
                  <button onClick={() => setStep(4)} className="rounded-xl bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 transition-all active:scale-[0.98]">
                    {t('bulkAwards.viewResults', 'View Results')}
                  </button>
                )}
              </div>
            </div>

            {/* TICKET-2 — created but not emailed banner */}
            {(processor.state?.status === 'done' || processor.state?.status === 'cancelled') && !sendEmail && processor.stats.created > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                <h3 className="text-sm font-semibold text-amber-900">
                  {t('bulkAwards.notEmailedTitle', '{{n}} credentials created — but recipients were not emailed', { n: processor.stats.created })}
                </h3>
                <p className="mt-1 text-xs text-amber-800">
                  {t('bulkAwards.notEmailedBody', "They're live and verifiable, but recipients haven't been notified (the \"Send email notification\" box was off). You can send them all from My Credentials — no need to re-import.")}
                </p>
                <Link to="/my-awards" className="mt-3 inline-block rounded-lg bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-700">
                  {t('bulkAwards.sendThemNow', 'Send them now')}
                </Link>
              </div>
            )}

            {/* Success table */}
            {(processor.state?.results ?? []).some(r => r.status === 'created') && (
              <div className="rounded-2xl border border-emerald-200 bg-white shadow-card overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border-b border-emerald-100">
                  <h3 className="text-sm font-semibold text-emerald-800">&#10003; {t('bulkAwards.successTitle', 'Created')} ({processor.stats.created})</h3>
                </div>
                <div className="max-h-52 overflow-y-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-emerald-50/80">
                      <tr>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">#</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colName', 'Name')}</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colEmail', 'Email')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(processor.state?.results ?? []).filter(r => r.status === 'created').map((r, i) => (
                        <tr key={i} className="border-t border-emerald-50">
                          <td className="px-4 py-2 text-xs text-gray-400">{r.recipient.rowIndex + 1}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{r.recipient.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{r.recipient.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Failures table */}
            {(processor.state?.results ?? []).some(r => r.status === 'failed') && (
              <div className="rounded-2xl border border-red-200 bg-white shadow-card overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-red-50 border-b border-red-100">
                  <h3 className="text-sm font-semibold text-red-800">&#10007; {t('bulkAwards.failuresTitle', 'Failed')} ({processor.stats.failed})</h3>
                </div>
                <div className="max-h-52 overflow-y-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-red-50/80">
                      <tr>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">#</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colName', 'Name')}</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colEmail', 'Email')}</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colError', 'Error')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(processor.state?.results ?? []).filter(r => r.status === 'failed').map((r, i) => (
                        <tr key={i} className="border-t border-red-50">
                          <td className="px-4 py-2 text-xs text-gray-400">{r.recipient.rowIndex + 1}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{r.recipient.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{r.recipient.email}</td>
                          <td className="px-4 py-2 text-xs text-red-600">{r.error || 'Unknown error'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pending/processing table */}
            {(processor.state?.results ?? []).some(r => r.status === 'pending' || r.status === 'processing') && (
              <div className="rounded-2xl border border-gray-200 bg-white shadow-card overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-700">{t('bulkAwards.pendingTitle', 'Pending')} ({processor.stats.pending})</h3>
                </div>
                <div className="max-h-40 overflow-y-auto">
                  <table className="w-full text-left text-sm">
                    <tbody>
                      {(processor.state?.results ?? []).filter(r => r.status === 'pending' || r.status === 'processing').map((r, i) => (
                        <tr key={i} className={`border-t border-gray-50 ${r.status === 'processing' ? 'animate-pulse bg-primary-50/40' : ''}`}>
                          <td className="px-4 py-2 text-xs text-gray-400">{r.recipient.rowIndex + 1}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{r.recipient.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{r.recipient.email}</td>
                          <td className="px-4 py-2 text-xs">
                            {r.status === 'processing' ? <span className="text-primary-600 font-medium">{t('bulkAwards.statusProcessing', 'Processing...')}</span> : <span className="text-gray-400">{t('bulkAwards.statusPending', 'Pending')}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============ STEP 4 ============ */}
        {step === 4 && (
          <div className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: t('bulkAwards.total', 'Total'), value: processor.stats.total, color: 'text-gray-900' },
                { label: t('bulkAwards.created', 'Created'), value: processor.stats.created, color: 'text-emerald-600' },
                { label: t('bulkAwards.failed', 'Failed'), value: processor.stats.failed, color: 'text-red-600' },
                { label: t('bulkAwards.skipped', 'Skipped'), value: processor.stats.total - processor.stats.created - processor.stats.failed, color: 'text-gray-400' },
              ].map(card => (
                <div key={card.label} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-card text-center">
                  <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
                  <p className="mt-1 text-xs text-gray-500">{card.label}</p>
                </div>
              ))}
            </div>

            {/* Created awards table */}
            {processor.stats.created > 0 && (
              <div className="rounded-2xl border border-emerald-200 bg-white shadow-card overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 border-b border-emerald-100">
                  <h3 className="text-sm font-semibold text-emerald-800">&#10003; {t('bulkAwards.successTitle', 'Created')} ({processor.stats.created})</h3>
                  <button onClick={downloadResultsCsv} className="rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 transition-colors">
                    {t('bulkAwards.downloadResults', 'Download CSV')}
                  </button>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-emerald-50/80">
                      <tr>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">#</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colName', 'Name')}</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colEmail', 'Email')}</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colAwardUrl', 'Award')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(processor.state?.results ?? []).filter(r => r.status === 'created').map((r, i) => (
                        <tr key={i} className="border-t border-emerald-50">
                          <td className="px-4 py-2 text-xs text-gray-400">{r.recipient.rowIndex + 1}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{r.recipient.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{r.recipient.email}</td>
                          <td className="px-4 py-2 text-xs">
                            {r.awardId && (
                              <a href={`/awards/${r.awardId.split('/').pop()}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                                {t('bulkAwards.viewAward', 'View')}
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Failed awards table */}
            {processor.stats.failed > 0 && (
              <div className="rounded-2xl border border-red-200 bg-white shadow-card overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-red-50 border-b border-red-100">
                  <h3 className="text-sm font-semibold text-red-800">&#10007; {t('bulkAwards.failuresTitle', 'Failed')} ({processor.stats.failed})</h3>
                  <button onClick={downloadFailuresCsv} className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 transition-colors">
                    {t('bulkAwards.downloadFailures', 'Download Failures CSV')}
                  </button>
                </div>
                <div className="max-h-60 overflow-y-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-red-50/80">
                      <tr>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">#</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colName', 'Name')}</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colEmail', 'Email')}</th>
                        <th className="px-4 py-2 text-xs font-medium text-gray-500">{t('bulkAwards.colError', 'Error')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(processor.state?.results ?? []).filter(r => r.status === 'failed').map((r, i) => (
                        <tr key={i} className="border-t border-red-50">
                          <td className="px-4 py-2 text-xs text-gray-400">{r.recipient.rowIndex + 1}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{r.recipient.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{r.recipient.email}</td>
                          <td className="px-4 py-2 text-xs text-red-600 max-w-[250px] truncate" title={r.error || ''}>{r.error || 'Unknown error'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <button onClick={downloadResultsCsv} className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 active:scale-[0.98]">
                {t('bulkAwards.downloadResults', 'Download Results CSV')}
              </button>
              {processor.stats.failed > 0 && (
                <>
                  <button onClick={downloadFailuresCsv} className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-semibold text-gray-700 shadow-sm transition-all hover:bg-gray-50 active:scale-[0.98]">
                    {t('bulkAwards.downloadFailures', 'Download Failures CSV')}
                  </button>
                  <button onClick={retryFailures} className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs font-semibold text-amber-700 shadow-sm transition-all hover:bg-amber-100 active:scale-[0.98]">
                    {t('bulkAwards.retryFailures', 'Retry Failures')}
                  </button>
                </>
              )}
              <button onClick={resetWizard} className="rounded-xl bg-primary-600 px-6 py-2.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-primary-700 active:scale-[0.98]">
                {t('bulkAwards.newBatch', 'New Batch')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
